import {createSelector, MemoizedSelector} from '@ngrx/store';
import {selectDataSourcesAdLoaded, selectDataSourcesAdLoading} from "./dataSourcesAd/dataSourcesAd.selectors";
import {
  selectDataSourcesSiteLoaded,
  selectDataSourcesSiteLoading
} from "./dataSourcesSite/dataSourcesSite.selectors";
import {selectMatchingsLoading} from "./matchings/matchings.selectors";
import {selectSitesLoading} from "./init/init.selectors";
import {selectSpacesLoading} from "./spaces/spaces.selectors";
import {selectDataSourceDimensionsLoaded} from './dataSourceDimensions/dataSourceDimensions.selectors';
import {selectDataSourcesMetricsLoaded} from "./dataSourcesMetrics/dataSourcesMetrics.selectors";
import {selectChannelsLoaded} from "./channels/channels.selectors";
import {
  selectSavedReportsLoaded,
  selectSavedReportsLoading
} from "./savedReports/savedReports.selectors";
import {
  selectExportsDestinationLoaded,
  selectExportsDestinationLoading
} from "./exportsDestination/exportsDestination.selectors";
import {selectExportsLoading} from "./exports/exports.selectors";
import {selectTemplatesLoaded} from "./templates/templates.selectors";
import {Serializer, Serializers} from '../interfaces/serializer';
import {DataSetConditionalDimension, DataSetDimension, GeneralDimension} from '../interfaces/dimensions';
import {
  selectGeneralDimensionComponentObjects,
  selectGeneralDimensions,
  selectGeneralDimensionsLoaded
} from './generalDimensions/generalDimensions.selectors';
import {
  selectDataSetDimensionComponentObjects,
  selectDataSetDimensions,
  selectDataSetDimensionsLoaded
} from './dataSetDimensions/dataSetDimensions.selectors';
import {
  selectDataSetConditionalDimensionComponentObjects,
  selectDataSetConditionalDimensions,
  selectDataSetConditionalDimensionsLoaded
} from './dataSetConditionalDimensions/dataSetConditionalDimensions.selectors';
import {DimensionComponentObject, DimensionComponentObjects} from '../classes/dimension-component-object';
import {DataSetMetric} from '../interfaces/metrics';
import {MetricComponentObject, MetricComponentObjects} from '../classes/metric-component-object';
import {
  selectDataSetMetricComponentObjects,
  selectDataSetMetrics,
  selectDataSetMetricsLoaded,
  selectDataSetMetricsLoading
} from './dataSetMetrics/dataSetMetrics.selectors';
import {
  DatasetDimensionsGroupComponentObject,
  DatasetDimensionsGroupComponentObjects
} from '../classes/dataset-dimensions-group-component-object';
import {selectDataSourcesUsageState, selectDataSourceUsageComponentObjects} from './dataSourcesUsage/dataSourcesUsage.selectors';
import {DatasetUsageComponentObject, DatasetUsageComponentObjects} from '../classes/dataset-usage-component-object';
import {Sort} from '../libraries/sort';
import {selectDataSourceCustomComponentObjects} from './dataSourcesCustom/dataSourcesCustom.selectors';
import {DatasetMetricsGroupComponentObject, DatasetMetricsGroupComponentObjects} from '../classes/dataset-metrics-group-component-object';
import {DataSourcesUsagesState} from './dataSourcesUsage/dataSourcesUsage';

export const selectDataSourcesAdSiteLoading = createSelector(
  selectDataSourcesAdLoading,
  selectDataSourcesSiteLoading,
  (adLoading: boolean, siteLoading: boolean) => adLoading || siteLoading
);

export const selectDataSourcesAdSiteLoaded = createSelector(
  selectDataSourcesAdLoaded,
  selectDataSourcesSiteLoaded,
  (adLoaded: boolean, siteLoaded: boolean) => adLoaded && siteLoaded
);

export const selectMatchingsDataSourcesAdSiteStates = createSelector(
  selectDataSourcesAdSiteLoading,
  selectMatchingsLoading,
  selectDataSourcesAdSiteLoaded,
  (adSiteLoading: boolean, matchingsLoading: boolean, loaded: boolean) => ({
    loading: adSiteLoading || matchingsLoading,
    loaded: loaded
  })
);

export const selectSpacesSitesLoading = createSelector(
  selectSitesLoading,
  selectSpacesLoading,
  (sites: boolean, spaces: boolean) => sites || spaces
);

export const selectSavedReportsExportsDestinationLoaded = createSelector(
  selectSavedReportsLoaded,
  selectExportsDestinationLoaded,
  (savedReportsLoaded: boolean, exportsDestinationLoaded: boolean) => savedReportsLoaded && exportsDestinationLoaded
);

export const selectSavedReportsExportsDestinationLoading = createSelector(
  selectSavedReportsLoading,
  selectExportsDestinationLoading,
  (savedReportsLoading: boolean, exportsDestinationLoading: boolean) => savedReportsLoading || exportsDestinationLoading
);

export const selectSavedReportsExportsLoading = createSelector(
  selectSavedReportsLoading,
  selectExportsLoading,
  (savedReportsLoading: boolean, exportsLoading: boolean) => savedReportsLoading || exportsLoading
);

function _sortDimensions(dimensions: Serializers<GeneralDimension | DataSetDimension | DataSetConditionalDimension>): Serializers<GeneralDimension | DataSetDimension | DataSetConditionalDimension> {
  return dimensions.sort((a: Serializer<GeneralDimension | DataSetDimension | DataSetConditionalDimension>, b: Serializer<GeneralDimension | DataSetDimension | DataSetConditionalDimension>): number => Sort.alphaAsc(a.attributes.name, b.attributes.name));
}

function _sortDimensionComponentObjects(dimensions: DimensionComponentObjects): DimensionComponentObjects {
  return dimensions.sort((a: DimensionComponentObject, b: DimensionComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
}

export const selectDimensions: MemoizedSelector<object, Serializers<GeneralDimension | DataSetDimension | DataSetConditionalDimension>> = createSelector(
  selectGeneralDimensions,
  selectDataSetDimensions,
  selectDataSetConditionalDimensions,
  (general: Serializers<GeneralDimension>, datasetDimensions: Serializers<DataSetDimension>, datasetConditionalDimension: Serializers<DataSetConditionalDimension>): Serializers<GeneralDimension | DataSetDimension | DataSetConditionalDimension> => _sortDimensions([
    ...general,
    ...datasetDimensions,
    ...datasetConditionalDimension
  ])
);

export const selectDimensionComponentObjects: MemoizedSelector<object, DimensionComponentObjects> = createSelector(
  selectGeneralDimensionComponentObjects,
  selectDataSetDimensionComponentObjects,
  selectDataSetConditionalDimensionComponentObjects,
  (generalDimensions: DimensionComponentObjects, datasetDimensions: DimensionComponentObjects, datasetConditionalDimensions: DimensionComponentObjects): DimensionComponentObjects => _sortDimensionComponentObjects([
    ...generalDimensions,
    ...datasetDimensions,
    ...datasetConditionalDimensions
  ])
);

export const selectDimensionComponentObjectsWithoutGeneral: MemoizedSelector<object, DimensionComponentObjects> = createSelector(
  selectDataSetDimensionComponentObjects,
  selectDataSetConditionalDimensionComponentObjects,
  (datasetDimensions: DimensionComponentObjects, datasetConditionalDimensions: DimensionComponentObjects): DimensionComponentObjects => _sortDimensionComponentObjects([
    ...datasetDimensions,
    ...datasetConditionalDimensions
  ])
);

export const selectDimensionComponentObjectsWithoutDate: MemoizedSelector<object, DimensionComponentObjects> = createSelector(
  selectGeneralDimensionComponentObjects,
  selectDataSetDimensionComponentObjects,
  selectDataSetConditionalDimensionComponentObjects,
  (generalDimensions: DimensionComponentObjects, datasetDimensions: DimensionComponentObjects, datasetConditionalDimensions: DimensionComponentObjects): DimensionComponentObjects => _sortDimensionComponentObjects([
    ...generalDimensions,
    ...datasetDimensions,
    ...datasetConditionalDimensions
  ].filter((dimension: DimensionComponentObject): boolean => !['day', 'week', 'month', 'year'].includes(dimension.payload.attributes.slug)))
);

export const selectDimensionsLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectGeneralDimensionsLoaded,
  selectDataSetDimensionsLoaded,
  selectDataSetConditionalDimensionsLoaded,
  (generalDimensions: boolean, datasetDimensions: boolean, datasetConditionalDimensions: boolean): boolean => generalDimensions && datasetDimensions && datasetConditionalDimensions
);

function _sortMetrics(metrics: Serializers<DataSetMetric>): Serializers<DataSetMetric> {
  return metrics.sort((a: Serializer<DataSetMetric>, b: Serializer<DataSetMetric>): number => Sort.alphaAsc(a.attributes.name, b.attributes.name));
}

function _sortMetricComponentObjects(metrics: MetricComponentObjects): MetricComponentObjects {
  return metrics.sort((a: MetricComponentObject, b: MetricComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
}

export const selectMetrics: MemoizedSelector<object, Serializers<DataSetMetric>> = createSelector(
  selectDataSetMetrics,
  (datasetMetrics: Serializers<DataSetMetric>): Serializers<DataSetMetric> => _sortMetrics([
    ...datasetMetrics
  ])
);

export const selectMetricComponentObjects: MemoizedSelector<object, MetricComponentObjects> = createSelector(
  selectDataSetMetricComponentObjects,
  (datasetMetrics: MetricComponentObjects): MetricComponentObjects => _sortMetricComponentObjects([
    ...datasetMetrics
  ])
);

export const selectMetricsLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectDataSetMetricsLoaded,
  (datasetMetrics: boolean): boolean => datasetMetrics
);

export const selectMetricsLoading: MemoizedSelector<object, boolean> = createSelector(
  selectDataSetMetricsLoading,
  (datasetMetrics: boolean): boolean => datasetMetrics
);

export const selectBackgroundLoaded: MemoizedSelector<object, boolean> = createSelector(
  selectDimensionsLoaded,
  selectMetricsLoaded,
  selectChannelsLoaded,
  (
    dimensionsLoaded: boolean,
    metricsLoaded: boolean,
    channelsLoaded: boolean
  ) => dimensionsLoaded && metricsLoaded && channelsLoaded
);

export const selectAllDimensionsMetricsLoadedForCheckTemplates: MemoizedSelector<object, boolean> = createSelector(
  selectDataSourceDimensionsLoaded,
  selectDataSourcesMetricsLoaded,
  selectDimensionsLoaded,
  selectMetricsLoaded,
  selectTemplatesLoaded,
  (
    dataSourcesDimensionsLoaded: boolean,
    dataSourcesMetricsLoaded: boolean,
    dimensionsLoaded: boolean,
    metricsLoaded: boolean,
    templatesLoaded: boolean
  ) =>
    dimensionsLoaded &&
    metricsLoaded  &&
    dataSourcesDimensionsLoaded &&
    dataSourcesMetricsLoaded &&
    templatesLoaded
);

export const selectDimensionSourceGroups: MemoizedSelector<object, DatasetDimensionsGroupComponentObjects> = createSelector(
  selectDataSourceUsageComponentObjects,
  selectDataSourceCustomComponentObjects,
  (datasource: DatasetUsageComponentObjects, customDatasource: DatasetUsageComponentObjects) => [...datasource, ...customDatasource]
    .reduce((output: DatasetDimensionsGroupComponentObjects, source: DatasetUsageComponentObject): DatasetDimensionsGroupComponentObjects => {
      if (!output.find((group: DatasetDimensionsGroupComponentObject): boolean => group.payload.relationships.data_source.data.attributes.slug === source.datasetDimensionsGroups$.value.payload.relationships.data_source.data.attributes.slug)) {
        output.push(source.datasetDimensionsGroups$.value);
      }

      return output.sort((a: DatasetDimensionsGroupComponentObject, b: DatasetDimensionsGroupComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
    }, [] as DatasetDimensionsGroupComponentObjects)
);

export const selectMetricSourceGroups: MemoizedSelector<object, DatasetMetricsGroupComponentObjects> = createSelector(
  selectDataSourceUsageComponentObjects,
  selectDataSourceCustomComponentObjects,
  (datasource: DatasetUsageComponentObjects, customDatasource: DatasetUsageComponentObjects) => [...datasource, ...customDatasource]
    .reduce((output: DatasetMetricsGroupComponentObjects, source: DatasetUsageComponentObject): DatasetMetricsGroupComponentObjects => {
      if (!output.find((group: DatasetMetricsGroupComponentObject): boolean => group.payload.relationships.data_source.data.attributes.slug === source.datasetDimensionsGroups$.value.payload.relationships.data_source.data.attributes.slug)) {
        output.push(source.datasetMetricsGroups$.value);
      }

      return output.sort((a: DatasetMetricsGroupComponentObject, b: DatasetMetricsGroupComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
    }, [] as DatasetMetricsGroupComponentObjects)
);
